"use client";

/**
 * useCopytradingSocket - Real-time copytrading state sync
 *
 * Connects to the backend WebSocket server to receive real-time
 * copytrading state updates (pause/resume/follow/unfollow).
 *
 * This enables instant UI updates when actions are taken from
 * either the web app or Telegram bot.
 */

import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { io, Socket } from "socket.io-client";
import { getAccessToken } from "@privy-io/react-auth";
import { useAuth } from "./useAuth";

// Event types emitted by the backend
export interface CopytradingEvent {
  leaderId: string;
  leaderAddress?: string;
  displayName?: string;
  isNewFollow?: boolean;
  timestamp: string;
}

export type CopytradingEventType =
  | "copytrading:leader_paused"
  | "copytrading:leader_resumed"
  | "copytrading:leader_followed"
  | "copytrading:leader_unfollowed";

interface UseCopytradingSocketOptions {
  /** Callback when a leader is paused */
  onLeaderPaused?: (event: CopytradingEvent) => void;
  /** Callback when a leader is resumed */
  onLeaderResumed?: (event: CopytradingEvent) => void;
  /** Callback when a leader is followed */
  onLeaderFollowed?: (event: CopytradingEvent) => void;
  /** Callback when a leader is unfollowed */
  onLeaderUnfollowed?: (event: CopytradingEvent) => void;
  /** Generic callback for any copytrading event */
  onAnyEvent?: (type: CopytradingEventType, event: CopytradingEvent) => void;
  /** Auto-connect when user is logged in (default: true) */
  autoConnect?: boolean;
}

interface UseCopytradingSocketReturn {
  /** Whether the socket is connected */
  isConnected: boolean;
  /** Connection error if any */
  error: string | null;
  /** Manually connect to the socket */
  connect: () => Promise<void>;
  /** Manually disconnect from the socket */
  disconnect: () => void;
}

// Backend WebSocket URL (same origin, path /ws)
const getWebSocketUrl = () => {
  if (typeof window === "undefined") return "";
  // In production, use the backend URL directly
  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_WS_URL ||
    (window.location.hostname === "localhost"
      ? "http://localhost:4000"
      : window.location.hostname.includes("staging")
        ? "https://staging-api.frenflow.com"
        : "https://api.frenflow.com");
  return backendUrl;
};

export function useCopytradingSocket(
  options: UseCopytradingSocketOptions = {}
): UseCopytradingSocketReturn {
  const {
    onLeaderPaused,
    onLeaderResumed,
    onLeaderFollowed,
    onLeaderUnfollowed,
    onAnyEvent,
    autoConnect = true,
  } = options;

  const { isLoggedIn, userId } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);

  // Store callbacks in refs to avoid recreating `connect` on every render
  const callbacksRef = useRef({
    onLeaderPaused,
    onLeaderResumed,
    onLeaderFollowed,
    onLeaderUnfollowed,
    onAnyEvent,
  });
  callbacksRef.current = {
    onLeaderPaused,
    onLeaderResumed,
    onLeaderFollowed,
    onLeaderUnfollowed,
    onAnyEvent,
  };

  // Fetch WebSocket token from backend
  const fetchWsToken = useCallback(async (): Promise<string | null> => {
    try {
      // Get Privy access token for authentication
      const accessToken = await getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch("/api/ws/token", {
        method: "POST",
        headers,
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.token;
    } catch (err) {
      return null;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (socketRef.current?.connected) {
      return;
    }

    if (!isLoggedIn || !userId) {
      setError("Not logged in");
      return;
    }

    try {
      setError(null);

      // Get WebSocket token
      const token = await fetchWsToken();
      if (!token) {
        setError("Failed to get WebSocket token");
        return;
      }

      const wsUrl = getWebSocketUrl();
      if (!wsUrl) return;

      // Create socket connection
      const socket = io(wsUrl, {
        path: "/ws",
        transports: ["websocket", "polling"],
        auth: { token },
        reconnection: false, // We handle reconnection manually
      });

      socketRef.current = socket;

      // Connection events
      socket.on("connect", () => {
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      });

      socket.on("disconnect", (reason) => {
        setIsConnected(false);

        // Auto-reconnect with exponential backoff
        if (reason !== "io client disconnect") {
          scheduleReconnect();
        }
      });

      socket.on("connect_error", (err) => {
        setError(err.message);
        setIsConnected(false);
        scheduleReconnect();
      });

      // Copytrading events — use refs to avoid stale closures
      socket.on("copytrading:leader_paused", (event: CopytradingEvent) => {
        callbacksRef.current.onLeaderPaused?.(event);
        callbacksRef.current.onAnyEvent?.("copytrading:leader_paused", event);
      });

      socket.on("copytrading:leader_resumed", (event: CopytradingEvent) => {
        callbacksRef.current.onLeaderResumed?.(event);
        callbacksRef.current.onAnyEvent?.("copytrading:leader_resumed", event);
      });

      socket.on("copytrading:leader_followed", (event: CopytradingEvent) => {
        callbacksRef.current.onLeaderFollowed?.(event);
        callbacksRef.current.onAnyEvent?.("copytrading:leader_followed", event);
      });

      socket.on("copytrading:leader_unfollowed", (event: CopytradingEvent) => {
        callbacksRef.current.onLeaderUnfollowed?.(event);
        callbacksRef.current.onAnyEvent?.("copytrading:leader_unfollowed", event);
      });

      socket.connect();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to connect");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, userId, fetchWsToken]);

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const attempts = reconnectAttemptsRef.current;
    const delay = Math.min(1000 * Math.pow(2, attempts), 30000); // Max 30s

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      connect();
    }, delay);
  }, [connect]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Auto-connect when logged in
  useEffect(() => {
    if (autoConnect && isLoggedIn && userId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, isLoggedIn, userId, connect, disconnect]);

  return {
    isConnected,
    error,
    connect,
    disconnect,
  };
}

export default useCopytradingSocket;
